import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Button } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import useUpdateTutorialSeen from '@/api/useUpdateTutorialSeen.ts';
import { useQueryClient } from '@tanstack/react-query';

// Import images
import firstImage from '@/assets/first.svg';
import secondImage from '@/assets/second.svg';
import thirdImage from '@/assets/third.svg';
import fifthImage from '@/assets/fifth.svg';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface OnboardingSlide {
  title: string;
  subtitle?: string;
  image: string;
}

const slides: OnboardingSlide[] = [
  {
    title: 'Personalised Dashboard',
    subtitle: 'Enter your subjects, exam boards and target grades so your dashboard is tailored just for you.',
    image: firstImage,
  },
  {
    title: 'Practice area with hints and model answers',
    subtitle:
      'Before sitting a full past paper, practise exam-style questions one by one, using hints and model answers to help you perfect your answers. Option to complete an AI practice mock paper before attempting a full past paper.',
    image: secondImage,
  },
  {
    title: 'Full Past Papers',
    subtitle:
      'Complete a full past paper under timed exam conditions. Get instant AI-generated feedback and grades, with weak areas highlighted and clear next steps to improve.',
    image: thirdImage,
  },
  {
    title: 'Progress tracker',
    subtitle:
      'See your progress and watch your grades rise as you practise and perfect your exam technique. Share your progress with parents whenever you need to.',
    image: fifthImage,
  },
];

const OnboardingPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const { mutateAsync: updateTutorialSeen } = useUpdateTutorialSeen();

  const handleSkip = () => {
    void (async () => {
      try {
        await updateTutorialSeen();
        // Refetch user query and wait for it to complete
        await queryClient.refetchQueries({ queryKey: ['user'] });
        navigate(Routes.STUDENT_ONBOARDING);
      } catch (error) {
        console.error('Failed to update tutorial status:', error);
        // Navigate anyway even if update fails
        navigate(Routes.STUDENT_ONBOARDING);
      }
    })();
  };

  const handleNext = () => {
    if (swiperInstance) {
      if (isLastSlide) {
        void (async () => {
          try {
            await updateTutorialSeen();
            // Refetch user query and wait for it to complete
            await queryClient.refetchQueries({ queryKey: ['user'] });
            navigate(Routes.STUDENT_ONBOARDING);
          } catch (error) {
            console.error('Failed to update tutorial status:', error);
            // Navigate anyway even if update fails
            navigate(Routes.STUDENT_ONBOARDING);
          }
        })();
      } else {
        swiperInstance.slideNext();
      }
    }
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setIsLastSlide(swiper.isEnd);
  };

  return (
    <BrandCardSideContent>
      <style>
        {`
          .onboarding-swiper .swiper-pagination-bullet {
            background-color: #D1D5DB;
            opacity: 1;
            width: 8px;
            height: 8px;
          }
          .onboarding-swiper .swiper-pagination-bullet-active {
            background-color: #2563EB;
          }
        `}
      </style>
      <div className="w-full max-w-[600px] flex flex-col gap-6">
        <Swiper
          modules={[Pagination, Navigation]}
          pagination={{
            clickable: true,
          }}
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          className="w-full onboarding-swiper"
          style={{ paddingBottom: '48px' }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="flex flex-col items-center gap-8 text-center">
                <div className="flex flex-col gap-2 px-8">
                  <h2 className="text-2xl font-varela font-normal leading-[117%] tracking-[0px]">{slide.title}</h2>
                  {slide.subtitle && (
                    <p className="text-sm leading-[143%] tracking-[0.17px] text-text-primary">
                      {index === 0 ? (
                        <>
                          For now, <span className="font-bold">only English</span>, more subjects coming soon.
                        </>
                      ) : (
                        slide.subtitle
                      )}
                    </p>
                  )}
                </div>
                <div className="w-full max-w-[360px] flex items-center justify-center px-4">
                  <img src={slide.image} alt={slide.title} className="w-full h-auto object-contain" />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="flex flex-col gap-3 w-full max-w-[440px] mx-auto px-6">
          <Button onClick={handleNext} color="primary" className="w-full">
            {isLastSlide ? 'Done' : 'Next'}
          </Button>
          {!isLastSlide && (
            <Button
              onClick={handleSkip}
              variant="outline"
              className="w-full bg-white hover:bg-white hover:text-current"
            >
              Skip
            </Button>
          )}
        </div>
      </div>
    </BrandCardSideContent>
  );
};

export default OnboardingPage;
